"use client";

import { useLayoutEffect } from "react";
import { gsap, loadScrollTrigger, loadSplitText } from "../../lib/gsap";
import { usePathname } from "next/navigation"; // ✅ NEW

export default function RevealOnScroll() {
  const pathname = usePathname(); // ✅ NEW

  useLayoutEffect(() => {
    // ✅ Scope all animations/triggers so they're auto-killed on route change
    const ctx = gsap.context(async () => {
      const ScrollTrigger = await loadScrollTrigger();
      const SplitText = await loadSplitText();
      
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const singles = gsap.utils.toArray<HTMLElement>(
          ".reveal:not(.reveal-stagger)",
        );
        singles.forEach((el) => {
          gsap.set(el, { opacity: 0, y: 24, willChange: "transform, opacity" });
          gsap.to(el, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
            clearProps: "all",
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
              once: true,
              fastScrollEnd: true,
            },
          });
        });

        const groups = gsap.utils.toArray<HTMLElement>(".reveal-stagger");
        groups.forEach((group) => {
          const items = group.querySelectorAll<HTMLElement>(".reveal-item");
          if (!items.length) return;

          gsap.set(items, { opacity: 0, y: 16, willChange: "transform, opacity" });
          gsap.to(items, {
            opacity: 1,
            y: 0,
            duration: 0.5,
            stagger: 0.06,
            ease: "power2.out",
            clearProps: "all",
            scrollTrigger: {
              trigger: group,
              start: "top 80%",
              once: true,
              fastScrollEnd: true,
            },
          });
        });
      });

      const images = gsap.utils.toArray<HTMLElement>(".reveal-img");
      images.forEach((img) => {
        gsap.set(img, { clipPath: "inset(100% 0% 0% 0%)", opacity: 0, willChange: "clip-path, opacity" });
        gsap.to(img, {
          clipPath: "inset(0% 0% 0% 0%)",
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: img,
            start: "top 85%",
            once: true,
            fastScrollEnd: true,
          },
        });
      });


      const counters = gsap.utils.toArray<HTMLElement>(".reveal-counter");
      const getNum = (v: string | undefined, fallback: number) =>
        v != null && v !== "" && !Number.isNaN(+v) ? +v : fallback;

      const formatNumber = (value: number, el: HTMLElement) => {
        const decimals = getNum(el.dataset.decimals, 0);
        const locale = el.dataset.locale || undefined;
        const style =
          (el.dataset.style as Intl.NumberFormatOptions["style"]) || "decimal";
        const currency = el.dataset.currency || undefined;
        return new Intl.NumberFormat(locale, {
          style,
          currency,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value);
      };

      counters.forEach((el) => {
        const from = getNum(el.dataset.from, 0);
        const to = getNum(el.dataset.to, 100);
        const duration = getNum(el.dataset.duration, 1.2);
        const ease = el.dataset.ease || "power2.out";
        const prefix = el.dataset.prefix || "";
        const suffix = el.dataset.suffix || "";

        gsap.set(el, { opacity: 0, y: 12 });
        const obj = { val: from };

        gsap
          .timeline({
            scrollTrigger: { trigger: el, start: "top 65%", once: true },
          })
          .to(el, {
            opacity: 1,
            y: 0,
            duration: Math.min(0.5, duration * 0.4),
            ease: "power2.out",
            clearProps: "opacity,transform",
          })
          .to(
            obj,
            {
              val: to,
              duration,
              ease,
              onUpdate: () => {
                el.textContent = `${prefix}${formatNumber(obj.val, el)}${suffix}`;
              },
            },
            0,
          );
      });

      // ensure triggers compute from the new DOM
      ScrollTrigger.refresh();
    });

    // cleanup on route change
    return () => ctx.revert();
  }, [pathname]); // ✅ re-init on every route
  //           ^^^^^^^
  // Key fix: re-run & clean when the path changes

  return null;
}
